import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Col, Descriptions, Flex, Form, Input, InputNumber, Modal, Radio, Result, Row, Select, Skeleton, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { ApiError, api, unwrap } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useTabTitle } from "@/components/tabs/WorkTabs";
import { useI18n } from "@/i18n";
import { useActiveWarehouse, useWarehouses } from "@/lib/useWarehouses";
import { SkuSelect } from "../catalog/mapping";
import { SupplierSelect, rupiahInput, taxPercent } from "./common";
import { VR_REASONS, VrStatusTag, type VendorReturn } from "./VendorReturns";

type Line = VendorReturn["lines"][number];
type Condition = Line["condition"];
type FormLine = { skuId?: string; condition?: Condition; quantity?: number; unitPrice?: number | null; fromLocationId?: string | null; note?: string };
type FormValues = {
  supplierId?: string;
  warehouseId?: string;
  settlement: VendorReturn["settlement"];
  reason: VendorReturn["reason"];
  taxRateBp?: number;
  note?: string;
  lines: FormLine[];
};

const CONDITIONS: Condition[] = ["damaged", "quarantine", "sellable"];
const TAX_CHOICES = [0, 1100, 1200];

/**
 * A return to supplier (Plan §6.18): written as a draft, made ready (stock
 * set aside), shipped, then settled by a credit note or replacement goods.
 * /app/procurement/returns/new starts one.
 */
export function VendorReturnDetail() {
  const { returnID = "" } = useParams();
  const isNew = returnID === "new";
  const { t } = useI18n();
  const { can } = useAuth();
  const errorText = useErrorText();
  const vr = useQuery({
    queryKey: ["vendor-returns", "detail", returnID],
    queryFn: () => unwrap(api.GET("/v1/vendor-returns/{returnID}", { params: { path: { returnID } } })),
    enabled: !isNew,
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
  });
  useTabTitle(isNew ? t("vr.add") : vr.data?.number);

  if (isNew) return can("procurement.manage") ? <Editor /> : <Result status="403" title={t("page.forbidden.title")} />;
  if (vr.isError) {
    return vr.error instanceof ApiError && vr.error.status === 404 ? <Result status="404" title={t("page.notFound.title")} /> : <Result status="error" title={errorText(vr.error)} />;
  }
  if (!vr.data) return <Skeleton active />;
  if (vr.data.status === "draft" && can("procurement.manage")) return <Editor vr={vr.data} />;
  return <View vr={vr.data} />;
}

function useRefresh() {
  const queryClient = useQueryClient();
  return () => {
    for (const k of ["vendor-returns", "inventory", "finance"]) void queryClient.invalidateQueries({ queryKey: [k] });
  };
}

function useLocations(warehouseId: string | undefined, wms: boolean) {
  return useQuery({
    queryKey: ["locations", warehouseId, "", ""],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/locations", { params: { path: { warehouseID: warehouseId! } } })),
    enabled: !!warehouseId && wms,
  });
}

function Editor({ vr }: { vr?: VendorReturn }) {
  const { t, formatMoney } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const navigate = useNavigate();
  const refresh = useRefresh();
  const warehouses = useWarehouses();
  const { current } = useActiveWarehouse();
  const [form] = Form.useForm<FormValues>();
  const warehouseId = Form.useWatch("warehouseId", form);
  const lines = Form.useWatch("lines", form) ?? [];
  const taxRateBp = Form.useWatch("taxRateBp", form) ?? 0;
  const wms = !!warehouses.accessible.find((w) => w.id === warehouseId)?.wmsEnabled;
  const locations = useLocations(warehouseId, wms);
  // From Barang rusak: ?lines=skuId:qty,... starts the return with them.
  const [params] = useSearchParams();
  const fromDamage = (params.get("lines") ?? "")
    .split(",")
    .map((x) => x.split(":"))
    .filter(([id, n]) => id && Number(n) > 0)
    .map(([skuId, n]) => ({ skuId, condition: "damaged" as const, quantity: Number(n) }));

  const initial: FormValues = vr
    ? {
        supplierId: vr.supplierId,
        warehouseId: vr.warehouseId,
        settlement: vr.settlement,
        reason: vr.reason,
        taxRateBp: vr.taxRateBp,
        note: vr.note,
        lines: vr.lines.map((l) => ({ skuId: l.skuId, condition: l.condition, quantity: l.quantity, unitPrice: l.unitPrice, fromLocationId: l.fromLocationId, note: l.note })),
      }
    : { warehouseId: current?.id, settlement: "credit", reason: "damaged", taxRateBp: 0, lines: fromDamage.length ? fromDamage : [{ condition: "damaged" }] };

  const subtotal = lines.reduce((sum, l) => sum + (l?.quantity ?? 0) * (l?.unitPrice ?? 0), 0);
  const tax = Math.floor((subtotal * taxRateBp + 5000) / 10000);

  const save = useMutation({
    mutationFn: async ({ v, ready }: { v: FormValues; ready: boolean }) => {
      const body = {
        supplierId: v.supplierId!,
        warehouseId: v.warehouseId!,
        settlement: v.settlement,
        reason: v.reason,
        taxRateBp: v.taxRateBp ?? 0,
        note: v.note?.trim() ?? "",
        lines: v.lines
          .filter((l) => l.skuId && l.quantity)
          .map((l) => ({
            skuId: l.skuId!,
            condition: l.condition ?? "damaged",
            quantity: l.quantity!,
            unitPrice: l.unitPrice ?? null,
            fromLocationId: wms ? (l.fromLocationId ?? null) : null,
            note: l.note?.trim() ?? "",
          })),
      };
      const saved = vr
        ? await unwrap(api.PUT("/v1/vendor-returns/{returnID}", { params: { path: { returnID: vr.id } }, body }))
        : await unwrap(api.POST("/v1/vendor-returns", { body }));
      if (ready) return unwrap(api.POST("/v1/vendor-returns/{returnID}/ready", { params: { path: { returnID: saved.id } } }));
      return saved;
    },
    onSuccess: (saved, { ready }) => {
      void message.success(ready ? t("vr.readyOk") : t("po.draftSaved"));
      refresh();
      if (!vr) navigate(`/app/procurement/returns/${saved.id}`, { replace: true });
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      initialValues={initial}
      onValuesChange={(changed: Partial<FormValues>) => {
        if (changed.supplierId) {
          void (async () => {
            const s = await unwrap(api.GET("/v1/suppliers/{supplierID}", { params: { path: { supplierID: changed.supplierId! } } }));
            form.setFieldsValue({ taxRateBp: s.pkp ? 1100 : 0 });
          })();
        }
      }}
    >
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space align="center" size={12} wrap>
          <Typography.Title level={2} className="z-num" style={{ margin: 0, fontSize: 28 }}>
            {vr?.number ?? t("vr.add")}
          </Typography.Title>
          {vr && <VrStatusTag status={vr.status} />}
        </Space>
        <Space wrap>
          {vr && <CancelButton vr={vr} />}
          <Button loading={save.isPending && !save.variables?.ready} onClick={() => form.validateFields().then((v) => save.mutate({ v, ready: false }))}>
            {t("po.saveDraft")}
          </Button>
          <Button type="primary" loading={save.isPending && !!save.variables?.ready} onClick={() => form.validateFields().then((v) => save.mutate({ v, ready: true }))}>
            {t("vr.makeReady")}
          </Button>
        </Space>
      </Flex>
      <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("vr.readyHelp")} />
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="supplierId" label={t("po.supplier")} rules={[{ required: true, message: t("field.required") }]}>
              <SupplierSelect />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="warehouseId" label={t("vr.fromWarehouse")} rules={[{ required: true, message: t("field.required") }]}>
              <Select options={warehouses.accessible.map((w) => ({ value: w.id, label: w.name }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="settlement" label={t("vr.settlement")}>
              <Radio.Group
                options={[
                  { value: "credit", label: t("vr.settlement.credit") },
                  { value: "replace", label: t("vr.settlement.replace") },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="reason" label={t("vr.reason")}>
              <Select options={VR_REASONS.map((r) => ({ value: r, label: t(`vr.reason.${r}`) }))} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="taxRateBp" label={t("po.tax")}>
              <Select options={TAX_CHOICES.map((bp) => ({ value: bp, label: bp ? t("po.taxRate", { rate: taxPercent(bp) }) : t("po.taxNone") }))} />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Card size="small" title={t("po.lines")} style={{ marginBottom: 16 }}>
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <>
              {fields.map((f) => {
                const l = lines[f.name] ?? {};
                return (
                  <Flex key={f.key} gap={8} align="start" wrap>
                    <Form.Item name={[f.name, "skuId"]} style={{ flex: "1 1 260px" }} rules={[{ required: true, message: t("field.required") }]}>
                      <SkuSelect singleOnly />
                    </Form.Item>
                    <Form.Item name={[f.name, "condition"]} tooltip={t("vr.conditionHelp")}>
                      <Select style={{ width: 150 }} options={CONDITIONS.map((c) => ({ value: c, label: t(`inventory.condition.${c}`) }))} />
                    </Form.Item>
                    <Form.Item name={[f.name, "quantity"]} rules={[{ required: true, message: t("field.required") }]}>
                      <InputNumber min={1} max={1_000_000} precision={0} placeholder={t("po.quantity")} style={{ width: 100 }} />
                    </Form.Item>
                    <Form.Item name={[f.name, "unitPrice"]} tooltip={t("vr.unitPriceHelp")}>
                      <InputNumber<number> min={0} max={1_000_000_000_000} precision={0} prefix="Rp" placeholder={t("vr.unitPriceAuto")} style={{ width: 160 }} {...rupiahInput} />
                    </Form.Item>
                    {wms && (
                      <Form.Item name={[f.name, "fromLocationId"]} tooltip={t("vr.fromLocationHelp")}>
                        <Select
                          allowClear
                          placeholder={t("vr.fromLocation")}
                          style={{ width: 170 }}
                          loading={locations.isPending}
                          showSearch={{ filterOption: (input, o) => String(o?.label ?? "").toLowerCase().includes(input.toLowerCase()) }}
                          options={(locations.data?.items ?? []).filter((x) => x.status === "active").map((x) => ({ value: x.id, label: x.code }))}
                        />
                      </Form.Item>
                    )}
                    <Typography.Text className="z-num" style={{ width: 130, textAlign: "right", paddingTop: 5 }}>
                      {formatMoney((l.quantity ?? 0) * (l.unitPrice ?? 0))}
                    </Typography.Text>
                    {fields.length > 1 && <Button type="text" icon={<MinusCircleOutlined />} aria-label={t("common.delete")} onClick={() => remove(f.name)} />}
                  </Flex>
                );
              })}
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ condition: "damaged" })} block>
                {t("po.addLine")}
              </Button>
            </>
          )}
        </Form.List>
        <Totals subtotal={subtotal} tax={tax} taxRateBp={taxRateBp} />
      </Card>
      <Form.Item name="note" label={t("po.note")}>
        <Input.TextArea rows={3} maxLength={1000} />
      </Form.Item>
    </Form>
  );
}

function Totals({ subtotal, tax, taxRateBp }: { subtotal: number; tax: number; taxRateBp: number }) {
  const { t, formatMoney } = useI18n();
  const row = (label: string, v: number, strong?: boolean) => (
    <Flex gap={24}>
      <Typography.Text type={strong ? undefined : "secondary"} strong={strong}>
        {label}
      </Typography.Text>
      <Typography.Text strong={strong} className="z-num" style={{ minWidth: 140, textAlign: "right" }}>
        {formatMoney(v)}
      </Typography.Text>
    </Flex>
  );
  return (
    <Flex vertical align="end" gap={4} style={{ marginTop: 16 }}>
      {row(t("po.subtotal"), subtotal)}
      {taxRateBp > 0 && row(t("po.taxRate", { rate: taxPercent(taxRateBp) }), tax)}
      {row(t("vr.total"), subtotal + tax, true)}
    </Flex>
  );
}

function CancelButton({ vr }: { vr: VendorReturn }) {
  const { t } = useI18n();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefresh();
  const [reason, setReason] = useState("");
  if (vr.status !== "draft" && vr.status !== "ready") return null;
  return (
    <Button
      danger
      onClick={() =>
        modal.confirm({
          title: t("vr.cancelConfirm", { number: vr.number }),
          content: <Input.TextArea rows={2} maxLength={500} placeholder={t("po.cancelReason")} onChange={(e) => setReason(e.target.value)} />,
          okText: t("vr.cancel"),
          okButtonProps: { danger: true },
          cancelText: t("common.close"),
          onOk: async () => {
            try {
              await unwrap(api.POST("/v1/vendor-returns/{returnID}/cancel", { params: { path: { returnID: vr.id } }, body: { reason } }));
              refresh();
            } catch (err) {
              void message.error(errorText(err));
            }
          },
        })
      }
    >
      {t("vr.cancel")}
    </Button>
  );
}

function View({ vr }: { vr: VendorReturn }) {
  const { t, formatMoney, formatNumber, formatDateTime } = useI18n();
  const { can, me } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const navigate = useNavigate();
  const refresh = useRefresh();
  const tz = me?.workspace?.timezone;
  const [shipping, setShipping] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const manage = can("procurement.manage");
  const whWork = manage || can("warehouse.manage");
  const path = { params: { path: { returnID: vr.id } } };
  const owed = vr.lines.reduce((s, l) => s + Math.max(l.shipped - l.replaced, 0), 0);
  // WMS: picked to KIRIM before it ships.
  const picking = vr.status === "ready" ? vr.picking : null;
  const picked = !picking || picking.status === "done";

  const act = useMutation<unknown, Error, "unready" | "close" | "receipt">({
    mutationFn: (kind) => {
      switch (kind) {
        case "unready":
          return unwrap(api.POST("/v1/vendor-returns/{returnID}/unready", path));
        case "close":
          return unwrap(api.POST("/v1/vendor-returns/{returnID}/close", path));
        case "receipt":
          return unwrap(api.POST("/v1/vendor-returns/{returnID}/receipts", path));
      }
    },
    onSuccess: (res, kind) => {
      refresh();
      const out = res as { receiptId?: string };
      if (kind === "receipt" && out.receiptId) navigate(`/app/warehouse/inbound/${out.receiptId}`);
    },
    onError: (err) => void message.error(errorText(err)),
  });

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space align="center" size={12} wrap>
          <Typography.Title level={2} className="z-num" style={{ margin: 0, fontSize: 28 }}>
            {vr.number}
          </Typography.Title>
          <VrStatusTag status={vr.status} />
          <Typography.Link onClick={() => navigate(`/app/procurement/suppliers/${vr.supplierId}`)}>{vr.supplierName}</Typography.Link>
        </Space>
        <Space wrap>
          {vr.status === "ready" && whWork && (
            <Button type="primary" disabled={!picked} onClick={() => setShipping(true)}>
              {t("vr.ship")}
            </Button>
          )}
          {vr.status === "ready" && manage && (
            <Button loading={act.isPending && act.variables === "unready"} onClick={() => act.mutate("unready")}>
              {t("vr.unready")}
            </Button>
          )}
          {vr.status === "shipped" && vr.settlement === "replace" && owed > 0 && whWork && (
            <Button
              type="primary"
              loading={act.isPending && act.variables === "receipt"}
              onClick={() => (vr.wms ? act.mutate("receipt") : setReceiving(true))}
            >
              {vr.wms ? t("vr.openReceipt") : t("vr.receive")}
            </Button>
          )}
          {vr.status === "shipped" && manage && (
            <Button
              onClick={() =>
                modal.confirm({
                  title: t("vr.close"),
                  content: t("vr.closeConfirm", { number: vr.number }),
                  okText: t("vr.close"),
                  cancelText: t("common.cancel"),
                  onOk: () => act.mutateAsync("close"),
                })
              }
            >
              {t("vr.close")}
            </Button>
          )}
          {manage && <CancelButton vr={vr} />}
        </Space>
      </Flex>
      {vr.status === "ready" && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("vr.readyNote", { warehouse: vr.warehouseName })} />}
      {picking && (
        <Alert
          type={picked ? "success" : "info"}
          showIcon
          style={{ marginBottom: 16 }}
          title={t(picked ? "vr.pickedNote" : "vr.pickingNote", { number: picking.number, done: picking.linesDone, total: picking.linesTotal })}
          action={
            can("warehouse.operate") && (
              <Button size="small" onClick={() => navigate(`/app/warehouse/tasks/${picking.taskId}`)}>
                {t("vr.openPicking")}
              </Button>
            )
          }
        />
      )}
      {vr.status === "shipped" && vr.settlement === "credit" && vr.credit && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title={t("vr.creditNote", { number: vr.credit.number, left: formatMoney(vr.credit.amount - vr.credit.used) })}
          action={
            can("finance.view") && (
              <Button size="small" onClick={() => navigate(`/app/finance/credits?open=${vr.credit!.id}`)}>
                {t("vr.openCredit")}
              </Button>
            )
          }
        />
      )}
      {vr.status === "shipped" && vr.settlement === "replace" && owed > 0 && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("vr.replaceOwed", { n: formatNumber(owed) })} />
      )}
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={8}>
          <Card size="small">
            <Descriptions
              column={1}
              size="small"
              items={[
                { key: "wh", label: t("vr.fromWarehouse"), children: vr.warehouseName },
                { key: "reason", label: t("vr.reason"), children: t(`vr.reason.${vr.reason}`) },
                { key: "settle", label: t("vr.settlement"), children: t(`vr.settlement.${vr.settlement}`) },
                { key: "tax", label: t("po.tax"), children: vr.taxRateBp ? t("po.taxRate", { rate: taxPercent(vr.taxRateBp) }) : t("po.taxNone") },
                { key: "by", label: t("po.createdBy"), children: `${vr.createdName} · ${formatDateTime(vr.createdAt, tz)}` },
                ...(vr.shippedAt ? [{ key: "ship", label: t("vr.shippedAt"), children: `${vr.shippedName} · ${formatDateTime(vr.shippedAt, tz)}` }] : []),
                ...(vr.shipNote ? [{ key: "shipNote", label: t("vr.shipNote"), children: vr.shipNote }] : []),
                ...(vr.credit
                  ? [{ key: "cn", label: t("vr.credit"), children: `${vr.credit.number} · ${formatMoney(vr.credit.used)} / ${formatMoney(vr.credit.amount)}` }]
                  : []),
                ...(vr.note ? [{ key: "note", label: t("po.note"), children: <span style={{ whiteSpace: "pre-wrap" }}>{vr.note}</span> }] : []),
                ...(vr.cancelReason ? [{ key: "cancel", label: t("po.cancelReason"), children: vr.cancelReason }] : []),
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} xl={16}>
          <Card size="small" title={t("po.lines")}>
            <Table<Line>
              size="small"
              rowKey="id"
              dataSource={vr.lines}
              pagination={false}
              scroll={{ x: 640 }}
              columns={[
                {
                  title: t("catalog.code"),
                  render: (_, l) => (
                    <span>
                      <span className="z-num">{l.skuCode}</span>
                      <br />
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {l.skuName}
                      </Typography.Text>
                    </span>
                  ),
                },
                {
                  title: t("inventory.condition"),
                  dataIndex: "condition",
                  width: 150,
                  render: (c: Condition, l) => (
                    <Space size={4} wrap>
                      <Tag color={c === "sellable" ? "green" : c === "damaged" ? "red" : "orange"}>{t(`inventory.condition.${c}`)}</Tag>
                      {l.fromCode && <Tag className="z-num">{l.fromCode}</Tag>}
                    </Space>
                  ),
                },
                { title: t("po.quantity"), dataIndex: "quantity", width: 80, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
                { title: t("po.unitPrice"), dataIndex: "unitPrice", width: 130, align: "right", render: (v: number) => <span className="z-num">{formatMoney(v)}</span> },
                { title: t("po.amount"), dataIndex: "amount", width: 140, align: "right", render: (v: number) => <span className="z-num">{formatMoney(v)}</span> },
                ...(vr.settlement === "replace" && vr.status !== "draft" && vr.status !== "ready"
                  ? [{ title: t("vr.replaced"), dataIndex: "replaced", width: 100, align: "right" as const, render: (v: number, l: Line) => <span className="z-num">{formatNumber(v)} / {formatNumber(l.shipped)}</span> }]
                  : []),
              ]}
            />
            <Totals subtotal={vr.subtotal} tax={vr.tax} taxRateBp={vr.taxRateBp} />
          </Card>
          {vr.receipts.length > 0 && (
            <Card size="small" title={t("vr.receipts")} style={{ marginTop: 16 }}>
              <Table
                size="small"
                rowKey="id"
                dataSource={vr.receipts}
                pagination={false}
                onRow={(r) => (vr.wms && can("warehouse.operate") ? { onClick: () => navigate(`/app/warehouse/inbound/${r.id}`), style: { cursor: "pointer" } } : {})}
                columns={[
                  { title: t("wms.in.number"), dataIndex: "number", width: 140, render: (v: string) => <span className="z-num">{v}</span> },
                  { title: t("common.status"), dataIndex: "status", width: 130, render: (v: string) => t(`wms.in.status.${v as "open"}`) },
                  { title: t("po.received"), dataIndex: "received", width: 100, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
                  { title: t("po.createdBy"), render: (_, r) => `${r.createdName} · ${formatDateTime(r.completedAt ?? r.createdAt, tz)}` },
                ]}
              />
            </Card>
          )}
        </Col>
      </Row>
      <ShipModal vr={vr} open={shipping} onClose={() => setShipping(false)} />
      <ReceiveModal vr={vr} open={receiving} onClose={() => setReceiving(false)} />
    </>
  );
}

function ShipModal({ vr, open, onClose }: { vr: VendorReturn; open: boolean; onClose: () => void }) {
  const { t, formatNumber } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefresh();
  const [note, setNote] = useState("");
  const [serials, setSerials] = useState<Record<string, string>>({});
  const serialLines = vr.wms ? vr.lines.filter((l) => l.tracking === "serial") : [];
  const ship = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/vendor-returns/{returnID}/ship", {
          params: { path: { returnID: vr.id } },
          body: {
            shipNote: note.trim(),
            serials: Object.fromEntries(serialLines.map((l) => [l.id, (serials[l.id] ?? "").split(/[\s,]+/).filter(Boolean)])),
          },
        }),
      ),
    onSuccess: () => {
      void message.success(t("vr.shipped"));
      refresh();
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal open={open} title={t("vr.shipTitle", { number: vr.number })} okText={t("vr.ship")} cancelText={t("common.cancel")} confirmLoading={ship.isPending} onOk={() => ship.mutate()} onCancel={onClose} destroyOnHidden>
      <Typography.Paragraph type="secondary">{t(vr.wms ? "vr.shipHelpWms" : "vr.shipHelp")}</Typography.Paragraph>
      <Input.TextArea rows={2} maxLength={500} placeholder={t("vr.shipNotePlaceholder")} value={note} onChange={(e) => setNote(e.target.value)} />
      {serialLines.map((l) => (
        <div key={l.id} style={{ marginTop: 12 }}>
          <Typography.Text>
            {t("vr.serialsFor", { code: l.skuCode, n: formatNumber(l.quantity) })}
          </Typography.Text>
          <Input.TextArea rows={2} className="z-num" value={serials[l.id] ?? ""} onChange={(e) => setSerials((x) => ({ ...x, [l.id]: e.target.value }))} />
        </div>
      ))}
    </Modal>
  );
}

function ReceiveModal({ vr, open, onClose }: { vr: VendorReturn; open: boolean; onClose: () => void }) {
  const { t, formatNumber } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefresh();
  const owed = new Map<string, { code: string; name: string; qty: number }>();
  for (const l of vr.lines) {
    const n = l.shipped - l.replaced;
    if (n <= 0) continue;
    const cur = owed.get(l.skuId);
    owed.set(l.skuId, { code: l.skuCode, name: l.skuName, qty: (cur?.qty ?? 0) + n });
  }
  const rows = [...owed.entries()].map(([skuId, x]) => ({ skuId, ...x }));
  const [qty, setQty] = useState<Record<string, number | null>>({});
  const value = (skuId: string, def: number) => (skuId in qty ? qty[skuId] : def);
  const receive = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/vendor-returns/{returnID}/receive", {
          params: { path: { returnID: vr.id } },
          body: { lines: rows.map((r) => ({ skuId: r.skuId, quantity: value(r.skuId, r.qty) ?? 0 })).filter((l) => l.quantity > 0) },
        }),
      ),
    onSuccess: () => {
      void message.success(t("po.receivedOk"));
      refresh();
      setQty({});
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const total = rows.reduce((s, r) => s + (value(r.skuId, r.qty) ?? 0), 0);
  return (
    <Modal open={open} width={640} title={t("vr.receive")} okText={t("vr.receive")} cancelText={t("common.cancel")} okButtonProps={{ disabled: total === 0 }} confirmLoading={receive.isPending} onOk={() => receive.mutate()} onCancel={onClose} destroyOnHidden>
      <Typography.Paragraph type="secondary">{t("vr.receiveHelp", { warehouse: vr.warehouseName })}</Typography.Paragraph>
      <Table
        size="small"
        rowKey="skuId"
        dataSource={rows}
        pagination={false}
        columns={[
          { title: t("catalog.code"), dataIndex: "code", render: (v: string) => <span className="z-num">{v}</span> },
          { title: t("common.name"), dataIndex: "name" },
          { title: t("vr.owed"), dataIndex: "qty", width: 90, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
          {
            title: t("po.received"),
            width: 120,
            render: (_, r) => <InputNumber min={0} max={r.qty} precision={0} value={value(r.skuId, r.qty)} onChange={(v) => setQty((x) => ({ ...x, [r.skuId]: v }))} style={{ width: "100%" }} />,
          },
        ]}
      />
    </Modal>
  );
}
